import type { BillingOrganizationSummary } from '../../../types/adminBilling';

/**
 * Statuses under which Stripe still raises invoices.
 *
 * An allow-list, mirroring `BillingMapper.IsSubscriptionLive` on the API, and for the same reason:
 * an unrecognised status has to read as "not billing" so the screens warn rather than reassure.
 * `paused` is the case that makes it matter - a trial that ended with no payment method raises
 * nothing, and a deny-list of cancelled-and-expired would count it as live.
 */
export const BILLING_SUBSCRIPTION_STATUSES = new Set([
  'active',
  'trialing',
  'incomplete',
  'past_due',
]);

/**
 * Whether this organization is fully set up to be billed.
 *
 * **In its own module, not beside the guide that first needed it.** The setup wizard and the guide
 * both read it to decide which of the two the page should show, and exporting it from a component
 * file breaks React Fast Refresh, which is what `react-refresh/only-export-components` is for.
 *
 * One definition, read by both, because two would eventually disagree - and the way that shows up
 * is a wizard that will not go away, or a guide claiming everything is in place while nothing is
 * being billed.
 */
export const isBillingSetupComplete = (
  summary: BillingOrganizationSummary | null
): boolean => {
  if (!summary?.plan) return false;

  return (
    summary.activeToContactCount > 0
    && summary.isStripeManaged
    && summary.plan.stripeSubscriptionId !== null
    && summary.plan.stripeSubscriptionStatus !== null
    && BILLING_SUBSCRIPTION_STATUSES.has(summary.plan.stripeSubscriptionStatus)
  );
};
